import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Alert,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import {
  type Problem,
  UpsertSubscriberSuspensionStatusSuspendedReason,
  type SubscriberResponse,
} from '../../api/model';
import {
  useUISubscribersUpsertSubscriberSpeedProfile,
  useUISubscribersUpsertSubscriberStaticIpAddresses,
  useUISubscribersUpsertSubscriberSuspensionStatus,
} from '../../api/subscribers-ui/subscribers-ui.ts';

type EditSubscriberFormData = {
  usoc: string;
  is_suspended: boolean;
  suspended_reason: string;
  ip_addresses: string;
};

type EditSubscriberFormProps = {
  subscriber: SubscriberResponse;
  onSuccess: () => void;
  onClose: () => void;
};

export const EditSubscriberForm = ({ subscriber, onSuccess, onClose }: EditSubscriberFormProps) => {
  const [formError, setFormError] = useState('');

  const { control, handleSubmit, watch, setValue } = useForm<EditSubscriberFormData>({
    defaultValues: {
      usoc: '',
      is_suspended: subscriber.is_suspended || false,
      suspended_reason: subscriber.suspended_reason || '',
      ip_addresses: subscriber.ip_addresses?.join('\n') || '',
    },
  });

  const { mutate: updateSpeedProfile, isPending: isUpdatingSpeedProfile } =
    useUISubscribersUpsertSubscriberSpeedProfile();

  const { mutate: updateSuspensionStatus, isPending: isUpdatingSuspension } =
    useUISubscribersUpsertSubscriberSuspensionStatus();

  const { mutate: updateStaticIps, isPending: isUpdatingIps } =
    useUISubscribersUpsertSubscriberStaticIpAddresses();

  const formValues = watch();
  const isPending = isUpdatingSpeedProfile || isUpdatingSuspension || isUpdatingIps;
  const isSuspended = formValues.is_suspended;

  // Set default suspended_reason and clear USOC/IP fields when is_suspended becomes true
  useEffect(() => {
    if (formValues.is_suspended) {
      if (!formValues.suspended_reason) {
        setValue('suspended_reason', UpsertSubscriberSuspensionStatusSuspendedReason['non-payment']);
      }
      // Clear USOC and reset IP addresses to original values when switching to suspended
      setValue('usoc', '');
      setValue('ip_addresses', subscriber.ip_addresses?.join('\n') || '');
    }
  }, [formValues.is_suspended, formValues.suspended_reason, setValue, subscriber.ip_addresses]);

  const handleError = (error: Problem) => {
    if (error.response?.data?.detail) {
      setFormError(error.response.data.detail);
    } else {
      setFormError('Failed to update subscriber. Please try again.');
    }
  };

  const onSubmit = (submitFormValues: EditSubscriberFormData) => {
    setFormError('');

    const updates: Array<Promise<void>> = [];

    // Update speed profile if a new USOC is provided
    if (submitFormValues.usoc.trim()) {
      updates.push(
        new Promise((resolve, reject) => {
          updateSpeedProfile(
            {
              username: subscriber.username,
              data: { USOC: submitFormValues.usoc.trim() },
            },
            {
              onSuccess: () => {
                resolve();
              },
              onError: (err) => {
                handleError(err);
                reject(err);
              },
            },
          );
        }),
      );
    }

    // Check if suspension status changed
    if (submitFormValues.is_suspended !== subscriber.is_suspended) {
      updates.push(
        new Promise((resolve, reject) => {
          updateSuspensionStatus(
            {
              username: subscriber.username,
              data: {
                is_suspended: submitFormValues.is_suspended,
                suspended_reason: submitFormValues.is_suspended
                  ? (submitFormValues.suspended_reason as UpsertSubscriberSuspensionStatusSuspendedReason)
                  : undefined,
              },
            },
            {
              onSuccess: () => {
                resolve();
              },
              onError: (err) => {
                handleError(err);
                reject(err);
              },
            },
          );
        }),
      );
    }

    // Check if suspension reason changed (only if suspended)
    if (
      submitFormValues.is_suspended &&
      submitFormValues.suspended_reason !== subscriber.suspended_reason &&
      subscriber.is_suspended
    ) {
      updates.push(
        new Promise((resolve, reject) => {
          updateSuspensionStatus(
            {
              username: subscriber.username,
              data: {
                is_suspended: true,
                suspended_reason:
                  submitFormValues.suspended_reason as UpsertSubscriberSuspensionStatusSuspendedReason,
              },
            },
            {
              onSuccess: () => {
                resolve();
              },
              onError: (err) => {
                handleError(err);
                reject(err);
              },
            },
          );
        }),
      );
    }

    // Check if IP addresses changed
    const newIps = submitFormValues.ip_addresses
      .split('\n')
      .map((ip) => ip.trim())
      .filter((ip) => ip.length > 0);
    const oldIps = subscriber.ip_addresses || [];
    const ipsChanged = newIps.length !== oldIps.length || newIps.some((ip, index) => ip !== oldIps[index]);

    if (ipsChanged) {
      updates.push(
        new Promise((resolve, reject) => {
          updateStaticIps(
            {
              username: subscriber.username,
              data: { ip_addresses: newIps },
            },
            {
              onSuccess: () => {
                resolve();
              },
              onError: (err) => {
                handleError(err);
                reject(err);
              },
            },
          );
        }),
      );
    }

    Promise.all(updates)
      .then(() => {
        setFormError('');
        onSuccess();
      })
      .catch(() => {
        // Error is already handled in individual promise rejections
      });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DialogTitle>Edit Subscriber: {subscriber.username}</DialogTitle>
      <DialogContent>
        {formError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}
        {isSuspended && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This subscriber is suspended. USOC and IP Address fields are read-only.
          </Alert>
        )}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            value={subscriber.speed_profile || 'Not set'}
            fullWidth
            label="Current Speed Profile"
            helperText="The speed profile currently assigned to this subscriber (read-only)"
            disabled
            slotProps={{
              input: {
                readOnly: true,
              },
            }}
          />

          <Controller
            name="usoc"
            control={control}
            render={({ field: { value, onChange, onBlur } }) => (
              <TextField
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                fullWidth
                label="New USOC"
                placeholder="Enter USOC code to update speed profile"
                helperText={
                  isSuspended
                    ? 'Cannot modify USOC while subscriber is suspended'
                    : "Enter a new USOC code to change the subscriber's speed profile. Leave empty to keep current."
                }
                slotProps={{ htmlInput: { maxLength: 32 } }}
                disabled={isPending || isSuspended}
              />
            )}
          />

          <FormControl fullWidth>
            <InputLabel>Suspension Status</InputLabel>
            <Controller
              name="is_suspended"
              control={control}
              render={({ field: { value, onChange, onBlur } }) => (
                <Select
                  value={String(value)}
                  onChange={(e) => onChange(e.target.value === 'true')}
                  onBlur={onBlur}
                  label="Suspension Status"
                  disabled={isPending}
                >
                  <MenuItem value="false">Active</MenuItem>
                  <MenuItem value="true">Suspended</MenuItem>
                </Select>
              )}
            />
            <FormHelperText>Mark subscriber as suspended or active</FormHelperText>
          </FormControl>

          {formValues.is_suspended && (
            <FormControl fullWidth>
              <InputLabel>Suspension Reason</InputLabel>
              <Controller
                name="suspended_reason"
                control={control}
                render={({ field: { value, onChange, onBlur } }) => (
                  <Select
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    label="Suspension Reason"
                    disabled={isPending}
                  >
                    <MenuItem value={UpsertSubscriberSuspensionStatusSuspendedReason['non-payment']}>
                      Non-payment
                    </MenuItem>
                    <MenuItem value={UpsertSubscriberSuspensionStatusSuspendedReason.abuse}>Abuse</MenuItem>
                  </Select>
                )}
              />
              <FormHelperText>Select reason for suspension</FormHelperText>
            </FormControl>
          )}

          <Controller
            name="ip_addresses"
            control={control}
            render={({ field: { value, onChange, onBlur } }) => (
              <TextField
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                fullWidth
                label="IP Addresses"
                placeholder="Enter IP addresses (one per line)"
                multiline
                rows={4}
                helperText={
                  isSuspended
                    ? 'Cannot modify IP addresses while subscriber is suspended'
                    : 'Enter one IP address per line'
                }
                disabled={isPending || isSuspended}
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={isPending}
          startIcon={isPending ? <CircularProgress size={20} /> : undefined}
        >
          {isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </form>
  );
};
